import { useCallback, useState } from 'react';

import { get, set } from '../sessionStorage';
import { getViewSlice, isViewStateBlob, setViewSlice } from '../sessionStorage/viewState';

export type TableSortState = {
  key: string;
  order: 'asc' | 'desc';
} | null;

const isSortOrder = (value: unknown): value is 'asc' | 'desc' =>
  value === 'asc' || value === 'desc';

const parseSortState = (stored: unknown): TableSortState => {
  if (stored === null) return null;
  if (!stored || typeof stored !== 'object') return null;

  const { key, order } = stored as { key?: unknown; order?: unknown };
  if (typeof key !== 'string' || !key || !isSortOrder(order)) return null;

  return { key, order };
};

const readTableSortState = (storageKey: string): TableSortState => {
  const stored = get(storageKey, null);
  if (isViewStateBlob(stored)) {
    return parseSortState(getViewSlice(storageKey, 'sort'));
  }
  return parseSortState(stored);
};

const writeTableSortState = (storageKey: string, state: TableSortState) => {
  const stored = get(storageKey, null);
  if (isViewStateBlob(stored)) {
    setViewSlice(storageKey, 'sort', state);
    return;
  }
  set(storageKey, state);
};

/**
 * Persists ResponsiveTable sort state in sessionStorage for a given key.
 * A view blob key writes the `sort` slice; a dedicated key stores `{ key, order }`.
 * When `storageKey` is falsy, returns a no-op (null default, write skipped).
 */
const usePersistedTableSort = (storageKey?: string | null) => {
  const [defaultSortState] = useState<TableSortState>(() =>
    storageKey ? readTableSortState(storageKey) : null,
  );

  const onSortChange = useCallback(
    (state: TableSortState) => {
      if (!storageKey) return;
      writeTableSortState(storageKey, state);
    },
    [storageKey],
  );

  return { defaultSortState, onSortChange };
};

export default usePersistedTableSort;
